import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Loader2, Download, Upload, Plus, Filter, AlertCircle, FileText, CheckCircle2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function LeaveBalances() {
  const { user } = useAuth();
  const isAdminOrHR = ['tenant_admin', 'hr_admin', 'payroll_admin', 'super_admin'].includes((user?.role || '').toLowerCase());

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [year, setYear] = useState(new Date().getFullYear());
  const [selDept, setSelDept] = useState('');
  const [selLoc, setSelLoc] = useState('');
  const [selLeaveType, setSelLeaveType] = useState('');
  const [searchEmp, setSearchEmp] = useState('');

  const getInitialCycle = () => {
    try {
      const p = new URLSearchParams(window.location.search);
      const c = p.get('cycle') || p.get('month');
      if (c && /^\d{4}-\d{2}$/.test(c)) return c;
    } catch { /* */ }
    return new Date().toISOString().slice(0, 7);
  };

  const [cycle, setCycle] = useState(getInitialCycle);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showProcessorEntry, setShowProcessorEntry] = useState(false);
  const [computing, setComputing] = useState(false);
  const [showLopModal, setShowLopModal] = useState(false);
  const [selectedEmpForLop, setSelectedEmpForLop] = useState(null);
  const [overrideLopDays, setOverrideLopDays] = useState(0);
  const [lopRemarks, setLopRemarks] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        const p = new URLSearchParams(window.location.search);
        const urlComp = p.get('company_id');
        if (urlComp && cs.some(c => c.id === urlComp)) {
          setCompanyId(urlComp);
        } else if (cs.length === 1) {
          setCompanyId(cs[0].id);
        }
      } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [dRes, lRes] = await Promise.all([
          axios.get(`${API_URL}/masters/departments?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/locations?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDepartments(dRes.data || []);
        setLocations(lRes.data || []);
      } catch { /* */ }
    })();
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) {
      toast.error('Please select a company');
      return;
    }
    setLoading(true);
    try {
      // Always pass cycle — never pass year alone, the backend derives year from cycle
      const q = new URLSearchParams({ company_id: companyId, cycle });
      if (selDept) q.set('department_id', selDept);
      if (selLoc) q.set('location_id', selLoc);
      if (selLeaveType) q.set('leave_type', selLeaveType);

      const { data } = await axios.get(`${API_URL}/leave-policy/balances?${q.toString()}`, { withCredentials: true });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }, [companyId, cycle, selDept, selLoc, selLeaveType]);

  // Reload the entire grid whenever company OR cycle changes
  useEffect(() => {
    if (companyId) load();
  }, [companyId, cycle, load]);

  const filteredRows = rows.filter((r) => {
    if (!searchEmp.trim()) return true;
    const term = searchEmp.toLowerCase();
    return (
      (r.employee_name || '').toLowerCase().includes(term) ||
      (r.employee_code || '').toLowerCase().includes(term) ||
      (r.department || '').toLowerCase().includes(term)
    );
  });

  const exportCSV = () => {
    if (!filteredRows.length) return;
    const headers = [
      'Employee Code', 'Employee Name', 'Department', 'Location', 'Payroll Group',
      'Leave Type', 'Opening Balance', 'Earned', 'Availed', 'Adjusted',
      'Pending Approval', 'LOP Converted', 'Closing Balance', 'Period', 'Source', 'Last Updated By'
    ];
    const csvLines = [
      headers.join(','),
      ...filteredRows.map((r) => [
        `"${r.employee_code || ''}"`,
        `"${r.employee_name || ''}"`,
        `"${r.department || ''}"`,
        `"${r.location || ''}"`,
        `"${r.payroll_group || ''}"`,
        `"${r.leave_type || ''}"`,
        r.opening_balance ?? 0,
        r.earned ?? 0,
        r.availed ?? 0,
        r.adjusted ?? 0,
        r.pending_approval ?? 0,
        r.lop_converted ?? 0,
        r.closing_balance ?? 0,
        `"${r.period || ''}"`,
        `"${r.source || ''}"`,
        `"${r.last_updated_by || ''}"`
      ].join(','))
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave_balances_${companyId}_${cycle}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComputeCycleSummary = async () => {
    if (!companyId) return toast.error('Select a company first');
    setComputing(true);
    try {
      const res = await axios.post(`${API_URL}/leave-policy/cycle-summary/compute?company_id=${companyId}&cycle=${cycle}`, {}, { withCredentials: true });
      toast.success(res.data.message || `Computed 21st-20th cycle summary for ${res.data.computed_count} employees!`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compute cycle summary');
    } finally {
      setComputing(false);
    }
  };

  const handleSaveLopOverride = async () => {
    if (!selectedEmpForLop) return;
    if (!lopRemarks.trim()) return toast.error('Mandatory remarks required for LOP override');
    try {
      await axios.post(`${API_URL}/leave-policy/lop/override`, {
        company_id: companyId,
        cycle,
        employee_id: selectedEmpForLop.employee_id || selectedEmpForLop.id,
        override_lop_days: Number(overrideLopDays),
        remarks: lopRemarks.trim()
      }, { withCredentials: true });
      toast.success('LOP days overridden successfully');
      setShowLopModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to override LOP days');
    }
  };

  const handleExportLedgerCSV = () => {
    if (!companyId) return toast.error('Select a company first');
    window.open(`${API_URL}/leave-policy/ledger/export?company_id=${companyId}&cycle=${cycle}`, '_blank');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="leave-balances-page">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Wallet className="w-6 h-6" /></span>
            <h1 className="text-xl font-bold text-slate-900">21st–20th Cycle Leave &amp; LOP Engine</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            21st to 20th cycle window, CL/EL slab accruals based on prev cycle present days, comp-off netting, 20th cutoff regularization, and LOP mapping.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdminOrHR && (
            <>
              <button
                onClick={handleComputeCycleSummary}
                disabled={!companyId || computing}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
              >
                {computing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Auto-Compute Cycle
              </button>
              <button
                onClick={handleExportLedgerCSV}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white font-semibold px-3.5 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
              >
                <Download size={14} /> Full Audit Export
              </button>
              <button
                onClick={() => setShowProcessorEntry(true)}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold px-3.5 py-2 rounded-lg text-xs border border-indigo-200 disabled:opacity-50"
              >
                <Plus size={14} /> Processor Entry
              </button>
              <button
                onClick={() => setShowBulkUpload(true)}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
              >
                <Upload size={14} /> Opening Balances
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toolbar Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Company *</label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium"
          >
            <option value="">— Select Company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Cycle (21st-20th) *</label>
          <input
            type="month"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-mono"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Department</label>
          <select
            value={selDept}
            onChange={(e) => setSelDept(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-slate-50"
          >
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Leave Type</label>
          <select
            value={selLeaveType}
            onChange={(e) => setSelLeaveType(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-slate-50"
          >
            <option value="">All Leave Types</option>
            {['CL', 'SL', 'EL', 'PL', 'ML', 'CompOff', 'LOP'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Search Employee</label>
          <input
            value={searchEmp}
            onChange={(e) => setSearchEmp(e.target.value)}
            placeholder="Code or name..."
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          />
        </div>

        <div className="flex items-end gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center justify-center gap-1 shadow-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
          </button>
          {filteredRows.length > 0 && (
            <button
              onClick={exportCSV}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg px-3 py-1.5 text-xs border border-slate-200 flex items-center justify-center gap-1"
            >
              <Download size={14} /> CSV
            </button>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading leave balances...</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-600">No leave balances found.</p>
            <p className="text-xs text-slate-400 mt-1">Select a company and click <b>Load</b> to query leave records.</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3">Emp Code</th>
                  <th className="px-3 py-3">Employee Name</th>
                  <th className="px-3 py-3">Department</th>
                  <th className="px-3 py-3">Leave Type</th>
                  <th className="px-3 py-3 text-right">Opening Bal.</th>
                  <th className="px-3 py-3 text-right">Earned</th>
                  <th className="px-3 py-3 text-right">Availed</th>
                  <th className="px-3 py-3 text-right text-amber-700 bg-amber-50/60">Pending Appr.</th>
                  <th className="px-3 py-3 text-right text-indigo-700 bg-indigo-50/60">Comp Off</th>
                  <th className="px-3 py-3 text-right text-teal-700 bg-teal-50/60">Reg. Adj.</th>
                  <th className="px-3 py-3 text-right text-blue-700 bg-blue-50/60">EL Adj.</th>
                  <th className="px-3 py-3 text-right text-violet-700 bg-violet-50/60">CL Adj.</th>
                  <th className="px-3 py-3 text-right text-rose-700 bg-rose-50/60">LOP Converted</th>
                  <th className="px-3 py-3 text-right text-emerald-700 bg-emerald-50/80 font-bold">Closing Bal.</th>
                  <th className="px-3 py-3">Cycle</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3">Updated By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-3 py-2.5 font-mono font-bold text-indigo-700">{r.employee_code}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{r.employee_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.department}</td>
                    <td className="px-3 py-2.5 font-bold text-slate-800">{r.leave_type}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtINR(r.opening_balance)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtINR(r.earned)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtINR(r.availed)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-700 bg-amber-50/30">{fmtINR(r.pending_approval)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-indigo-700 bg-indigo-50/30">{fmtINR(r.approved_compoff)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-teal-700 bg-teal-50/30">{fmtINR(r.regularized_days)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-blue-700 bg-blue-50/30">{r.leave_type === 'EL' ? fmtINR(r.adjusted) : '0.00'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-violet-700 bg-violet-50/30">{r.leave_type === 'CL' ? fmtINR(r.adjusted) : '0.00'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-700 bg-rose-50/30">{fmtINR(r.lop_converted)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-700 bg-emerald-50/40 text-sm">
                      {fmtINR(r.closing_balance)}
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-slate-500 whitespace-nowrap">{r.period}</td>
                    <td className="px-3 py-2.5 text-[11px]">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">{r.source}</span>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-slate-500">{r.last_updated_by}</td>
                    {isAdminOrHR && (
                      <td className="px-4 py-2.5 text-[11px] text-right">
                        <button
                          onClick={() => { setSelectedEmpForLop(r); setOverrideLopDays(r.lop_converted || 0); setShowLopModal(true); }}
                          className="bg-amber-100 hover:bg-amber-200 text-amber-800 px-2.5 py-1 rounded font-bold"
                          title="Override LOP Days before payroll lock"
                        >
                          Override LOP
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin/HR Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal companyId={companyId} onClose={() => setShowBulkUpload(false)} onSuccess={() => { setShowBulkUpload(false); load(); }} />
      )}

      {/* HR Processor Entry Modal */}
      {showProcessorEntry && (
        <ProcessorEntryModal companyId={companyId} onClose={() => setShowProcessorEntry(false)} onSuccess={() => { setShowProcessorEntry(false); load(); }} />
      )}

      {/* LOP Manual Override Modal */}
      {showLopModal && selectedEmpForLop && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-amber-50 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-amber-900">Manual LOP Override</h3>
                <p className="text-xs text-amber-700">Pre-payroll lock manual override with mandatory audit remarks</p>
              </div>
              <button onClick={() => setShowLopModal(false)} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-xs font-semibold text-slate-800">{selectedEmpForLop.employee_name} ({selectedEmpForLop.employee_code})</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Cycle: <strong>{cycle}</strong> (21st M-1 to 20th M)</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Override LOP Days *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={overrideLopDays}
                  onChange={(e) => setOverrideLopDays(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-rose-700"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Mandatory Override Remarks *</label>
                <textarea
                  rows={3}
                  value={lopRemarks}
                  onChange={(e) => setLopRemarks(e.target.value)}
                  placeholder="Reason for manual LOP override (required)..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setShowLopModal(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
              <button
                onClick={handleSaveLopOverride}
                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm"
              >
                Save LOP Override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkUploadModal({ companyId, onClose, onSuccess }) {
  const [uploadMode, setUploadMode] = useState('current_month'); // current_month | period_wise | consolidated
  const [csvText, setCsvText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const TEMPLATES = {
    current_month: {
      filename: 'leave_balance_current_month_template.csv',
      buttonText: 'Download Current Month Template',
      headers: 'Employee Code,Employee Name,Leave Type,Payroll Cycle,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted (+/-),LOP Days,Closing Balance,Remarks',
      sample: `EMP001,John Doe,CL,2026-07,5,1,0,0,0,6,July Cycle Accrual
EMP002,Jane Smith,EL,2026-07,10,1,0,0,0,11,July Cycle Accrual`
    },
    period_wise: {
      filename: 'leave_balance_period_wise_template.csv',
      buttonText: 'Download Period-wise Template',
      headers: 'Employee Code,Employee Name,Leave Type,Period From,Period To,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted (+/-),Comp-Off Availed,LOP Days,Closing Balance,Remarks',
      sample: `EMP001,John Doe,EL,2026-01-01,2026-06-30,10,6,2,0,1,0,13,H1 Period Balance
EMP002,Jane Smith,CL,2026-01-01,2026-03-31,6,3,1,0,0,0,8,Q1 Period Balance`
    },
    consolidated: {
      filename: 'leave_balance_consolidated_template.csv',
      buttonText: 'Download Consolidated Template',
      headers: 'Employee Code,Employee Name,Department,Location,Payroll Group,Employee Category,Leave Type,Financial Year,Period,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted,Comp-Off Availed,LOP Converted,Closing Balance,Last Updated Date,Upload Batch No.,Remarks',
      sample: `EMP001,John Doe,Engineering,HQ-Main,Staff Monthly,Regular,CL,2026-2027,2026-07,5,1,0,0,0,0,6,2026-07-20,MIGRATION-01,Migration Import
EMP002,Jane Smith,HR,Branch-South,Executive,EL,2026-2027,2026-07,12,1,2,0,0,0,11,2026-07-20,MIGRATION-01,Migration Import`
    }
  };

  const currentTemplate = TEMPLATES[uploadMode];

  const downloadSample = () => {
    const csvContent = `${currentTemplate.headers}\n${currentTemplate.sample}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentTemplate.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCsvText(evt.target.result);
    };
    reader.readAsText(file);
  };

  const processUpload = async () => {
    if (!csvText.trim()) return toast.error('Please upload or paste CSV rows');
    setUploading(true);
    setResult(null);

    const lines = csvText.trim().split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      setUploading(false);
      return toast.error('CSV must contain a header row and at least one data row');
    }

    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = cols[idx] || '';
      });

      if (uploadMode === 'current_month') {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || obj['employee id'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          payroll_cycle: obj['payroll cycle'] || obj['payroll_cycle'] || obj['cycle'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted (+/-)'] || obj['leave adjusted'] || obj['adjusted'] || 0),
          lop_converted: Number(obj['lop days'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          remarks: obj['remarks'] || '',
        });
      } else if (uploadMode === 'period_wise') {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          period_from: obj['period from'] || obj['period_from'] || '',
          period_to: obj['period to'] || obj['period_to'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted (+/-)'] || obj['leave adjusted'] || obj['adjusted'] || 0),
          compoff_availed: Number(obj['comp-off availed'] || obj['compoff_availed'] || 0),
          lop_converted: Number(obj['lop days'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          remarks: obj['remarks'] || '',
        });
      } else {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          department: obj['department'] || '',
          location: obj['location'] || '',
          payroll_group: obj['payroll group'] || obj['payroll_group'] || '',
          employee_category: obj['employee category'] || obj['employee_category'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          financial_year: obj['financial year'] || obj['financial_year'] || '',
          period: obj['period'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted'] || obj['adjusted'] || 0),
          compoff_availed: Number(obj['comp-off availed'] || obj['compoff_availed'] || 0),
          lop_converted: Number(obj['lop converted'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          last_updated_date: obj['last updated date'] || obj['last_updated_date'] || '',
          upload_batch_no: obj['upload batch no.'] || obj['upload_batch_no'] || '',
          remarks: obj['remarks'] || '',
        });
      }
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/leave-policy/balances/bulk-upload`,
        { company_id: companyId, upload_mode: uploadMode, rows },
        { withCredentials: true }
      );
      setResult(data);
      if (data.successful_rows > 0) {
        toast.success(`Successfully imported ${data.successful_rows} leave balance records via ${uploadMode} engine!`);
      }
      if (data.failed_rows > 0) {
        toast.warning(`${data.failed_rows} rows contained errors. Download the error report below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload processing failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result?.error_report_csv) return;
    const blob = new Blob([result.error_report_csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upload_error_report_${result.batch_no}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">Admin / HR Leave Balance Bulk Upload</h3>
            <p className="text-xs text-slate-500">Dedicated templates &amp; specialized validation engines per upload type</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1 text-sm">
          {/* Mode Selector */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'current_month', label: '1. Current Month Upload' },
              { id: 'period_wise', label: '2. Period-wise Upload' },
              { id: 'consolidated', label: '3. Consolidated Upload' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { setUploadMode(m.id); setCsvText(''); setResult(null); }}
                className={`py-2.5 px-3 rounded-lg text-xs font-bold border text-center transition-all ${uploadMode === m.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <div>
              <p className="text-xs font-bold text-slate-800">
                {uploadMode === 'current_month' && 'Current Month Template (Single Payroll Cycle)'}
                {uploadMode === 'period_wise' && 'Period-wise Template (Historical / Multi-Period Range)'}
                {uploadMode === 'consolidated' && 'Consolidated Migration Template (Full Master Import)'}
              </p>
              <p className="text-[11px] text-slate-500">Download the dedicated CSV template tailored for this upload type.</p>
            </div>
            <button
              onClick={downloadSample}
              className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-3.5 py-2 rounded-lg text-xs font-bold shadow-sm"
            >
              <Download size={14} /> {currentTemplate.buttonText}
            </button>
          </div>

          {/* Upload Input */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Upload {uploadMode === 'current_month' ? 'Current Month' : uploadMode === 'period_wise' ? 'Period-wise' : 'Consolidated'} CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="w-full border border-slate-300 rounded-lg p-2 text-xs bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">CSV Content Preview / Raw Editor</label>
            <textarea
              rows={6}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-3 text-xs font-mono"
              placeholder={`Paste ${uploadMode} CSV rows here...`}
            />
          </div>

          {/* Results Block */}
          {result && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between font-bold text-xs">
                <span>Engine: <code className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200 uppercase">{result.upload_mode}</code></span>
                <span>Batch No: <code className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-800">{result.batch_no}</code></span>
                <span className="text-emerald-700">Successful: {result.successful_rows}</span>
                <span className="text-rose-700">Failed: {result.failed_rows}</span>
              </div>
              {result.failed_rows > 0 && (
                <div className="pt-2 flex justify-end">
                  <button
                    onClick={downloadErrorReport}
                    className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm"
                  >
                    <Download size={14} /> Download {uploadMode} Error Report CSV
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
          <button
            disabled={uploading}
            onClick={processUpload}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50 flex items-center gap-1.5"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Validate &amp; Import ({uploadMode})
          </button>
        </div>
      </div>
    </div>
  );
}

function ProcessorEntryModal({ companyId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    employee_id: '', leave_type: 'CL', period_from: new Date().toISOString().slice(0, 7) + '-01', period_to: new Date().toISOString().slice(0, 10),
    entry_type: 'adjusted', amount: 1, reason: '',
  });
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/employees?company_id=${companyId}&limit=1000`, { withCredentials: true });
        setEmployees(data || []);
      } catch { /* */ }
    })();
  }, [companyId]);

  // Filter employees based on search term
  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const empCode = (emp.employment_details?.employee_code || '').toLowerCase();
    const firstName = (emp.personal_details?.first_name || '').toLowerCase();
    const lastName = (emp.personal_details?.last_name || '').toLowerCase();
    const fullName = (emp.personal_details?.full_name || `${firstName} ${lastName}`.trim()).toLowerCase();
    const department = (emp.employment_details?.department || '').toLowerCase();
    const mobile = (emp.personal_details?.mobile || '').toLowerCase();
    return empCode.includes(term) || fullName.includes(term) || department.includes(term) || mobile.includes(term);
  });

  // Helper to get employee display name and details
  const getEmployeeDisplayText = (emp) => {
    const empCode = emp.employment_details?.employee_code || '';
    const pd = emp.personal_details || {};
    const fullName = pd.full_name || `${pd.first_name || ''} ${pd.last_name || ''}`.trim() || empCode;
    const ed = emp.employment_details || {};
    const dept = ed.department || ed.department_name || '';
    const desig = ed.designation || ed.designation_name || '';
    return `${empCode} · ${fullName}${dept ? ` · ${dept}` : ''}${desig ? ` · ${desig}` : ''}`;
  };

  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    const selectedEmp = employees.find(e => e.id === empId);
    if (selectedEmp) {
      const pd = selectedEmp.personal_details || {};
      const ed = selectedEmp.employment_details || {};
      setForm(prev => ({
        ...prev,
        employee_id: empId,
        // Store extra details for display (optional but helpful)
        selected_employee_code: ed.employee_code || '',
        selected_employee_name: pd.full_name || `${pd.first_name || ''} ${pd.last_name || ''}`.trim(),
        selected_department: ed.department || ed.department_name || '',
        selected_designation: ed.designation || ed.designation_name || '',
        selected_location: ed.location || ed.location_name || '',
      }));
    } else {
      setForm(prev => ({
        ...prev,
        employee_id: '',
        selected_employee_code: '',
        selected_employee_name: '',
        selected_department: '',
        selected_designation: '',
        selected_location: '',
      }));
    }
  };

  const submit = async () => {
    if (!form.employee_id) return toast.error('Select an employee');
    if (!form.reason.trim()) return toast.error('Mandatory reason required for HR Processor Entry');

    setSaving(true);
    try {
      await axios.post(`${API_URL}/leave-policy/balances/processor-entry`, {
        company_id: companyId, ...form, amount: Number(form.amount)
      }, { withCredentials: true });
      toast.success('HR Processor entry posted successfully');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Processor entry failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">HR Direct Processor Entry</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Employee *</label>
            <input
              type="text"
              placeholder="Search employees by code, name, department, mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs mb-2"
            />
            <select
              value={form.employee_id}
              onChange={handleEmployeeSelect}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium"
            >
              <option value="">— Select Employee —</option>
              {filteredEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>{getEmployeeDisplayText(emp)}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Leave Type *</label>
              <select
                value={form.leave_type}
                onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-bold text-indigo-700"
              >
                {['CL', 'SL', 'EL', 'PL', 'ML', 'CompOff', 'LOP'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Adjustment Type *</label>
              <select
                value={form.entry_type}
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs capitalize font-semibold"
              >
                <option value="adjusted">Adjusted (+/-)</option>
                <option value="earned">Earned / Accrued (+)</option>
                <option value="availed">Availed (-)</option>
                <option value="lop">LOP Converted (-)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Period From</label>
              <input type="date" value={form.period_from} onChange={(e) => setForm({ ...form, period_from: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Period To</label>
              <input type="date" value={form.period_to} onChange={(e) => setForm({ ...form, period_to: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Amount (Days) *</label>
            <input
              type="number"
              step="0.5"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Mandatory Reason / Remarks *</label>
            <textarea
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs"
              placeholder="e.g. Approved HR manual adjustment for joining credit"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
          <button
            disabled={saving}
            onClick={submit}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50"
          >
            {saving ? 'Posting…' : 'Post Processor Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}
